import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { updateServiceArea } from '../services/serviceAreasService';
import { fetchAllContentsByPaginating } from '../services/productsService';
import {
  buildUrunCategoryTreeModel,
  leafIdsFromServiceArea,
  productIdsFromServiceArea,
  isServiceAreaFullProductScope,
} from '../utils/urunCategoryTree';
import { includesNormalized } from '../utils/searchHelpers';
import './ServiceAreaCategoryDetail.css';

const ROOT_PARENT = '__root__';

/**
 * Ana kategoriler + detay ağacı (kategori → alt kategori → ürün adları).
 * Her üst düzeyde tek kardeş açık (akordeon).
 */
export function ServiceAreaCategoryDetail({
  area,
  allCategories = [],
  onAreaUpdated,
  onAreaDeleted,
  confirm: confirmFn,
  readOnly = false,
  searchable = true,
  variant = 'cell',
  modalTitle,
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  /** parentId → açık olan tek çocuk id (kardeş akordeon) */
  const [openByParent, setOpenByParent] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [urunContents, setUrunContents] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const tree = useMemo(() => buildUrunCategoryTreeModel(allCategories), [allCategories]);
  const authorizedLeafIds = useMemo(() => leafIdsFromServiceArea(area), [area]);
  const authorizedProductIds = useMemo(() => productIdsFromServiceArea(area), [area]);

  const effectiveLeafIds = useMemo(() => {
    const s = new Set(authorizedLeafIds);
    if (authorizedProductIds.size === 0) return s;
    for (const p of urunContents) {
      const pid = String(p.id || p._id || '');
      if (!authorizedProductIds.has(pid)) continue;
      const kid = String(p.kategoriId || p.kategori?.id || p.kategori?._id || '');
      if (kid && tree.leafIds.has(kid)) s.add(kid);
    }
    return s;
  }, [authorizedLeafIds, authorizedProductIds, urunContents, tree]);

  const productsByCategoryId = useMemo(() => {
    const map = new Map();
    for (const p of urunContents) {
      const kid = String(p.kategoriId || p.kategori?.id || p.kategori?._id || '');
      if (!kid) continue;
      if (!map.has(kid)) map.set(kid, []);
      map.get(kid).push({
        id: String(p.id || p._id),
        ad: (p.icerikAdi || p.urunAdi || '—').trim(),
      });
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.ad.localeCompare(b.ad, 'tr'));
    }
    return map;
  }, [urunContents]);

  const mainRoots = useMemo(
    () => tree.rootIdsForLeafSet(effectiveLeafIds),
    [tree, effectiveLeafIds]
  );

  const districtLabel =
    area?.ilce?.adi || area?.ilce?.ad || area?.ilce?.name || 'İlçe';
  const cityLabel = area?.il?.name || area?.il?.ad || '';
  const title = modalTitle || (readOnly ? 'Ürün kapsamı' : 'Yetkili ürün kategorileri');
  const q = searchQuery.trim();

  const nodeLabel = useCallback(
    (node) => node?.kategoriAdi || node?.ad || '—',
    []
  );

  const productsForLeaf = useCallback(
    (leafId) => productsByCategoryId.get(String(leafId)) || [],
    [productsByCategoryId]
  );

  const leafMatchesSearch = useCallback(
    (leafId) => {
      if (!q) return true;
      const node = tree.nodesById.get(String(leafId));
      if (node && includesNormalized(nodeLabel(node), q)) return true;
      return productsForLeaf(leafId).some((p) => includesNormalized(p.ad, q));
    },
    [q, tree, nodeLabel, productsForLeaf]
  );

  const branchHasVisibleLeaf = useCallback(
    (nodeId) => {
      const cid = String(nodeId);
      if (tree.leafIds.has(cid)) {
        return effectiveLeafIds.has(cid) && leafMatchesSearch(cid);
      }
      for (const leaf of tree.leavesUnder(cid)) {
        if (effectiveLeafIds.has(leaf) && leafMatchesSearch(leaf)) return true;
      }
      return false;
    },
    [tree, effectiveLeafIds, leafMatchesSearch]
  );

  const countProductsUnderRoot = useCallback(
    (rootId) => {
      let n = 0;
      for (const leaf of tree.leavesUnder(rootId)) {
        if (!authorizedLeafIds.has(leaf)) continue;
        const prods = productsForLeaf(leaf);
        if (!q) {
          n += prods.length > 0 ? prods.length : 1;
        } else if (leafMatchesSearch(leaf)) {
          const matched = prods.filter((p) => includesNormalized(p.ad, q));
          n += matched.length > 0 ? matched.length : 1;
        }
      }
      return n;
    },
    [tree, authorizedLeafIds, productsForLeaf, q, leafMatchesSearch]
  );

  useEffect(() => {
    if (!detailOpen) return undefined;
    let cancelled = false;
    setProductsLoading(true);
    fetchAllContentsByPaginating({ icerikTipi: 'urun', aktif: true })
      .then((list) => {
        if (!cancelled) setUrunContents(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setUrunContents([]);
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detailOpen]);

  const visibleMainRoots = useMemo(() => {
    if (!q) return mainRoots;
    return mainRoots.filter((r) => branchHasVisibleLeaf(tree.catId(r)));
  }, [mainRoots, q, tree, branchHasVisibleLeaf]);

  const isAccordionOpen = useCallback(
    (parentId, childId) => openByParent[String(parentId)] === String(childId),
    [openByParent]
  );

  const toggleAccordion = useCallback((parentId, childId) => {
    const p = String(parentId);
    const c = String(childId);
    setOpenByParent((prev) => ({
      ...prev,
      [p]: prev[p] === c ? null : c,
    }));
  }, []);

  const openDetail = () => {
    setSearchQuery('');
    setOpenByParent({});
    setDetailOpen(true);
  };

  const handleRemoveLeaf = useCallback(
    async (leafId) => {
      const areaId = area?.id || area?._id;
      if (!areaId) return;

      const nextIds = [...authorizedLeafIds].filter((id) => id !== String(leafId));
      const nextProducts = [...authorizedProductIds];
      if (nextIds.length === 0 && nextProducts.length === 0) {
        const ok = confirmFn
          ? await confirmFn({
              title: 'Yetki alanını kaldır',
              message: `${districtLabel} için yetkili ürün kalmadı. Tüm yetki alanı kaydı silinsin mi?`,
              confirmLabel: 'Sil',
              cancelLabel: 'Vazgeç',
              variant: 'danger',
            })
          : window.confirm(
              `${districtLabel} için yetkili ürün kalmadı. Tüm yetki alanı kaydı silinsin mi?`
            );
        if (!ok) return;
        onAreaDeleted?.(areaId);
        setDetailOpen(false);
        return;
      }

      setSavingId(leafId);
      try {
        const res = await updateServiceArea(areaId, {
          yetkiliKategoriIds: nextIds,
          yetkiliUrunIcerikIds: nextProducts,
        });
        const updated = res?.veri?.yetkiAlani;
        if (updated) onAreaUpdated?.(updated);
        if (window.showNotification) {
          window.showNotification('success', 'Ürün yetkisi kaldırıldı');
        }
      } catch (err) {
        const msg =
          err?.data?.mesaj || err?.message || 'Ürün yetkisi kaldırılamadı';
        if (window.showNotification) window.showNotification('error', msg);
      } finally {
        setSavingId(null);
      }
    },
    [area, authorizedLeafIds, authorizedProductIds, districtLabel, onAreaDeleted, onAreaUpdated, confirmFn]
  );

  const countProductsUnderNode = useCallback(
    (nodeId) => {
      let n = 0;
      for (const leaf of tree.leavesUnder(nodeId)) {
        if (!authorizedLeafIds.has(leaf)) continue;
        const prods = productsForLeaf(leaf);
        if (!q) {
          n += prods.length > 0 ? prods.length : 1;
        } else if (leafMatchesSearch(leaf)) {
          const matched = prods.filter((p) => includesNormalized(p.ad, q));
          n += matched.length > 0 ? matched.length : 1;
        }
      }
      return n;
    },
    [tree, authorizedLeafIds, productsForLeaf, q, leafMatchesSearch]
  );

  const renderChildrenOf = (parentId, childIds) =>
    (childIds || [])
      .filter((id) => branchHasVisibleLeaf(String(id)))
      .map((id) => {
        const cid = String(id);
        if (tree.leafIds.has(cid)) {
          return renderLeafBlock(cid, parentId);
        }
        return renderGroupNode(cid, parentId);
      });

  const handleRemoveProduct = useCallback(
    async (productId, leafId) => {
      const areaId = area?.id || area?._id;
      if (!areaId) return;
      const pid = String(productId);
      const nextProducts = [...authorizedProductIds].filter((id) => id !== pid);
      const nextLeaves = [...authorizedLeafIds];
      if (nextProducts.length === 0 && nextLeaves.length === 0) {
        const ok = confirmFn
          ? await confirmFn({
              title: 'Yetki alanını kaldır',
              message: `${districtLabel} için yetkili ürün kalmadı. Tüm yetki alanı kaydı silinsin mi?`,
              confirmLabel: 'Sil',
              cancelLabel: 'Vazgeç',
              variant: 'danger',
            })
          : window.confirm(
              `${districtLabel} için yetkili ürün kalmadı. Tüm yetki alanı kaydı silinsin mi?`
            );
        if (!ok) return;
        onAreaDeleted?.(areaId);
        setDetailOpen(false);
        return;
      }
      setSavingId(pid);
      try {
        const res = await updateServiceArea(areaId, {
          yetkiliKategoriIds: nextLeaves,
          yetkiliUrunIcerikIds: nextProducts,
        });
        const updated = res?.veri?.yetkiAlani;
        if (updated) onAreaUpdated?.(updated);
        if (window.showNotification) window.showNotification('success', 'Ürün yetkisi kaldırıldı');
      } catch (err) {
        const msg = err?.data?.mesaj || err?.message || 'Ürün yetkisi kaldırılamadı';
        if (window.showNotification) window.showNotification('error', msg);
      } finally {
        setSavingId(null);
      }
    },
    [area, authorizedLeafIds, authorizedProductIds, districtLabel, onAreaDeleted, onAreaUpdated, confirmFn]
  );

  const renderLeafBlock = (leafId, parentId) => {
    const cid = String(leafId);
    if (!effectiveLeafIds.has(cid) || !leafMatchesSearch(cid)) return null;

    const node = tree.nodesById.get(cid);
    const allProds = productsForLeaf(cid);
    const leafFull = authorizedLeafIds.has(cid);
    const scopedProds = leafFull
      ? allProds
      : allProds.filter((p) => authorizedProductIds.has(String(p.id)));
    const visibleProds = q
      ? scopedProds.filter(
          (p) =>
            includesNormalized(p.ad, q) ||
            (node && includesNormalized(nodeLabel(node), q))
        )
      : scopedProds;
    const open = isAccordionOpen(parentId, cid);

    return (
      <div key={cid} className="service-area-cat-detail-tree__group">
        <div className="service-area-cat-detail-tree__group-head-row">
          <button
            type="button"
            className="service-area-cat-detail-tree__group-head"
            onClick={() => toggleAccordion(parentId, cid)}
          >
            <span className="service-area-cat-detail-tree__chevron">
              <i className={`fas fa-chevron-${open ? 'down' : 'right'}`} aria-hidden="true" />
            </span>
            <span className="service-area-cat-detail-tree__group-title">
              {node ? nodeLabel(node) : '—'}
            </span>
            <span className="service-area-cat-detail-tree__group-badge">
              {visibleProds.length} ürün
            </span>
          </button>
          {!readOnly && leafFull ? (
            <button
              type="button"
              className="service-area-cat-detail-tree__remove"
              title="Bu alt kategori yetkisini kaldır"
              disabled={!!savingId}
              onClick={() => handleRemoveLeaf(cid)}
            >
              {savingId === cid ? (
                <i className="fas fa-spinner fa-spin" aria-hidden="true" />
              ) : (
                <>
                  <i className="fas fa-times" aria-hidden="true" /> Kaldır
                </>
              )}
            </button>
          ) : null}
        </div>
        {open ? (
          <div className="service-area-cat-detail-tree__group-body">
            {productsLoading ? (
              <span className="service-area-cat-detail-tree__no-products">Yükleniyor…</span>
            ) : visibleProds.length > 0 ? (
              <ul className="service-area-cat-detail-tree__products">
                {visibleProds.map((p) => (
                  <li key={p.id} className="service-area-cat-detail-tree__product">
                    <span>{p.ad}</span>
                    {!readOnly && !leafFull ? (
                      <button
                        type="button"
                        className="service-area-cat-detail-tree__remove service-area-cat-detail-tree__remove--inline"
                        title="Ürün yetkisini kaldır"
                        disabled={!!savingId}
                        onClick={() => handleRemoveProduct(p.id, cid)}
                      >
                        {savingId === p.id ? (
                          <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                        ) : (
                          <i className="fas fa-times" aria-hidden="true" />
                        )}
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="service-area-cat-detail-tree__no-products">
                Bu kategoride kayıtlı ürün yok
              </span>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const renderGroupNode = (nodeId, parentId) => {
    const node = tree.nodesById.get(String(nodeId));
    if (!node) return null;
    const cid = String(nodeId);
    if (!branchHasVisibleLeaf(cid) || tree.leafIds.has(cid)) return null;

    const childIds = tree.childMap.get(cid) || [];
    const open = isAccordionOpen(parentId, cid);
    const itemCount = countProductsUnderNode(cid);

    return (
      <div key={cid} className="service-area-cat-detail-tree__group">
        <button
          type="button"
          className="service-area-cat-detail-tree__group-head"
          onClick={() => toggleAccordion(parentId, cid)}
        >
          <span className="service-area-cat-detail-tree__chevron">
            <i className={`fas fa-chevron-${open ? 'down' : 'right'}`} aria-hidden="true" />
          </span>
          <span className="service-area-cat-detail-tree__group-title">{nodeLabel(node)}</span>
          <span className="service-area-cat-detail-tree__group-badge">{itemCount} ürün</span>
        </button>
        {open ? (
          <div className="service-area-cat-detail-tree__group-body">
            {renderChildrenOf(cid, childIds)}
          </div>
        ) : null}
      </div>
    );
  };

  const totalProductCount = useMemo(() => {
    let n = 0;
    for (const leaf of authorizedLeafIds) {
      const prods = productsForLeaf(leaf);
      n += prods.length > 0 ? prods.length : 1;
    }
    return n;
  }, [authorizedLeafIds, productsForLeaf]);

  if (isServiceAreaFullProductScope(area)) {
    return <span className="service-area-cat-cell__muted">Tümü</span>;
  }

  const cellClass =
    variant === 'inline'
      ? 'service-area-cat-cell service-area-cat-cell--inline'
      : 'service-area-cat-cell';

  return (
    <>
      <div className={cellClass}>
        <div className="service-area-cat-cell__roots">
          {mainRoots.map((r) => (
            <span key={tree.catId(r)} className="service-area-cat-cell__tag">
              {r.kategoriAdi || r.ad}
            </span>
          ))}
        </div>
        <button
          type="button"
          className="icon-btn"
          title={readOnly ? 'Ürün kapsamı detayı' : 'Kategori ve ürün detayı'}
          aria-label="Kategori detayını aç"
          onClick={openDetail}
        >
          <i className="fas fa-sitemap" aria-hidden="true" />
        </button>
      </div>

      {detailOpen ? (
        <div
          className="modal-overlay service-area-cat-detail-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="service-area-cat-detail-title"
          onClick={() => setDetailOpen(false)}
        >
          <div
            className="modal-content service-area-cat-detail-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="service-area-cat-detail-title">{title}</h2>
              <button
                type="button"
                className="modal-close-btn service-area-cat-detail-modal__close"
                aria-label="Kapat"
                onClick={() => setDetailOpen(false)}
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </div>
            <div className="service-area-cat-detail-modal__body">
              <p className="service-area-cat-detail-modal__subtitle">
                <strong>{districtLabel}</strong>
                {cityLabel ? ` · ${cityLabel}` : ''}
                {' — '}
                {productsLoading ? '…' : totalProductCount} ürün
              </p>
              {searchable ? (
                <div className="service-area-cat-detail-modal__search">
                  <div className="service-area-cat-detail-modal__search-wrap">
                    <i className="fas fa-search" aria-hidden="true" />
                    <input
                      type="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Kategori veya ürün ara…"
                      aria-label="Kategori veya ürün ara"
                    />
                  </div>
                </div>
              ) : null}
              <div className="service-area-cat-detail-tree">
                {visibleMainRoots.length === 0 ? (
                  <p className="service-area-cat-detail-tree__empty">Arama ile eşleşen ürün bulunamadı.</p>
                ) : (
                  visibleMainRoots.map((root) => {
                    const rid = tree.catId(root);
                    const open = isAccordionOpen(ROOT_PARENT, rid);
                    const productCount = countProductsUnderRoot(rid);
                    const rootChildIds = tree.childMap.get(rid) || [];

                    return (
                      <div key={rid} className="service-area-cat-detail-tree__root">
                        <button
                          type="button"
                          className="service-area-cat-detail-tree__root-head"
                          onClick={() => toggleAccordion(ROOT_PARENT, rid)}
                        >
                          <span className="service-area-cat-detail-tree__chevron">
                            <i
                              className={`fas fa-chevron-${open ? 'down' : 'right'}`}
                              aria-hidden="true"
                            />
                          </span>
                          <span style={{ flex: 1 }}>{root.kategoriAdi || root.ad}</span>
                          <span className="service-area-cat-detail-tree__group-badge">
                            {productCount} ürün
                          </span>
                        </button>
                        {open ? (
                          <div className="service-area-cat-detail-tree__root-body">
                            {tree.leafIds.has(rid) && authorizedLeafIds.has(rid)
                              ? renderLeafBlock(rid, rid)
                              : null}
                            {renderChildrenOf(rid, rootChildIds)}
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
